import {FieldInputProps} from 'formik';
import {uniqBy} from 'lodash';
import {OVRecommendation} from '../../../schema-types/OVRecommendation';
import {OvbuilderDetails_ovrecommendDetails} from '../../../schema-types/OvbuilderDetails';
import {BuilderPot} from '../../../schema-types/BuilderPot';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {contributeFieldType, getContributeFromId} from './builderContributeFields';
import {includesSaltus} from '../../../utils/includesSaltus';

export const contributionSources = ({
  contributeFromPots,
  contributeTo,
  isSppDa,
}: {
  contributeFromPots: BuilderPot[];
  contributeTo?: string | null;
  isSppDa: boolean;
}): BuilderPot[] => {
  const filteredPots = contributeFromPots.filter((el) => {
    // All non-saltus plans in contribution sources should be hidden. Only show new cash contribution or Saltus plans.
    if (isSppDa && !el.isSaltus) {
      return false;
    }

    /**
     * removing a GIA Gross from sources if its parent is the selected destination
     */
    if (!!contributeTo && !!el.parent?.id && el.parent.id === contributeTo) {
      return false;
    }

    /**
     * we don't want the new shell plans appearing as possible source plans for
     * contribution recommendations.
     * No curoId implies this plan is a new shell plan.
     *
     * See more in jira ticket https://saltus.atlassian.net/browse/ON-4045
     */
    if (!el.curoId) {
      return false;
    }

    return true;
  });

  return uniqBy(filteredPots, 'id');
};
export const contributionDestinations = ({
  destinationPots,
  contributeFieldObj,
  builderDetails,
  isSPPDA,
}: {
  destinationPots: BuilderPot[];
  contributeFieldObj: FieldInputProps<contributeFieldType | undefined>;
  builderDetails: OvbuilderDetails_ovrecommendDetails | null;
  isSPPDA: boolean;
}): BuilderPot[] => {
  // const savedInRecc: BuilderPot[] = builderDetails?.contribute?.destinationPot
  //   ? [builderDetails.contribute.destinationPot]
  //   : [];
  // const destinations = uniqBy([...savedInRecc, ...destinationPots], 'id');

  return destinationPots.filter((el) => {
    const contributeFrom = contributeFieldObj.value?.contributeFrom;
    const fromId = getContributeFromId(contributeFrom);
    const fromParentId =
      contributeFrom?.sourceType === 'plan' ? contributeFrom.parentPotId : undefined;
    if (el.id === fromId || el.id === fromParentId) {
      return false;
    }
    if (el.isDummy && el.originalOvrId === builderDetails?.id) {
      return false;
    }
    if (isSPPDA && !el.isSaltus) {
      return false;
    }
    return true;
  });
};

export function getPlanTypeFromPot(
  pot: BuilderPot
): 'isa' | 'gia' | 'bond' | 'pension' | null {
  const name = pot.productName?.formattedName?.toLocaleLowerCase();
  if (name?.includes('isa')) {
    return 'isa';
  }
  if (name?.includes('bond')) {
    return 'bond';
  }
  if (name?.includes('gia')) {
    return 'gia';
  }
  if (name?.includes('pension')) {
    return 'pension';
  }
  return null;
}

export const contributionIsActuallyTransfer = ({
  values,
  pots,
}: {
  values: builderFieldsType;
  pots: BuilderPot[];
}) => {
  /*
  Add in “specie” and “full redemption” to contribution source panel if:
- ISA to ISA
- OR GIA to GIA
- OR BOTH provider is Saltus
    - AND ISA to GIA OR GIA to ISA
- OR if BOTH providers are the same
    - for ANY PLAN TYPES (AKA isa to bond, gia to pension, all of them)
  */
  const contributeFrom = values.contribute?.contributeFrom;
  // Only a plan source can really be a transfer; cash and bank sources are new money.
  if (values.contribute?.contributeTo && contributeFrom?.sourceType === 'plan') {
    const fromPot = pots.find((x) => x.id === contributeFrom.id)!;
    const toPot = pots.find((x) => x.id === values.contribute!.contributeTo)!;

    if (!fromPot || !toPot) {
      return false;
    }
    const fromPotType = getPlanTypeFromPot(fromPot);
    const toPotType = getPlanTypeFromPot(toPot);
    if (fromPotType === toPotType) {
      return true;
    }
    if (
      fromPot.productProvider &&
      toPot.productProvider &&
      fromPot.productProvider.id === toPot.productProvider?.id
    ) {
      if (
        includesSaltus(fromPot.productProvider?.name) &&
        includesSaltus(toPot.productProvider?.name)
      ) {
        if (fromPotType === 'gia' && toPotType === 'isa') {
          return true;
        }
        if (fromPotType === 'isa' && toPotType === 'gia') {
          return true;
        }
        return false;
      }
      // not saltus, always a transfer
      return true;
    }
  }
  return false;
};

export const shouldShowEmployerContribution = ({
  isMinor,
  mainPlanType,
}: {
  isMinor: boolean | undefined;
  mainPlanType: string | undefined;
}): boolean => {
  if (mainPlanType === 'pension') {
    return !isMinor;
  }
  return false;
};
